import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  readPatients,
  addPatient,
  updatePatient,
  deletePatient,
  readConsultations,
  addConsultation,
  updateConsultation,
  deleteConsultation,
  readVitals,
  addVitalsData,
  updateVitalsData,
  deleteVitalsData,
  readTreatments,
  addTreatment,
  updateTreatment,
  deleteTreatment,
  readMedicalHistory,
  addMedicalHistory,
  updateMedicalHistory,
  deleteMedicalHistory,
  readObservations,
  addObservation,
  updateObservation,
  deleteObservation
} from '../services/excelService';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const [patients, setPatients] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger les patients depuis Excel
  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readPatients();
      setPatients(data);
    } catch (error) {
      console.error('Erreur lors du chargement des patients :', error);
      setError('Erreur lors du chargement des patients');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les consultations depuis Excel
  const loadConsultations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readConsultations();
      setConsultations(data);
    } catch (error) {
      console.error('Erreur lors du chargement des consultations :', error);
      setError('Erreur lors du chargement des consultations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les constantes vitales depuis Excel
  const loadVitals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readVitals();
      setVitals(data);
    } catch (error) {
      console.error('Erreur lors du chargement des constantes vitales :', error);
      setError('Erreur lors du chargement des constantes vitales');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les traitements depuis Excel
  const loadTreatments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readTreatments();
      setTreatments(data);
    } catch (error) {
      console.error('Erreur lors du chargement des traitements :', error);
      setError('Erreur lors du chargement des traitements');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les antécédents médicaux depuis Excel
  const loadMedicalHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readMedicalHistory();
      setMedicalHistory(data);
    } catch (error) {
      console.error('Erreur lors du chargement des antécédents médicaux :', error);
      setError('Erreur lors du chargement des antécédents médicaux');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les observations depuis Excel
  const loadObservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = readObservations();
      setObservations(data);
    } catch (error) {
      console.error('Erreur lors du chargement des observations :', error);
      setError('Erreur lors du chargement des observations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger toutes les données
  const loadAllData = useCallback(async () => {
    await Promise.all([
      loadPatients(),
      loadConsultations(),
      loadVitals(),
      loadTreatments(),
      loadMedicalHistory(),
      loadObservations()
    ]);
  }, [loadPatients, loadConsultations, loadVitals, loadTreatments, loadMedicalHistory, loadObservations]);

  // Ajouter un patient
  const addPatientData = useCallback(async (patientData) => {
    try {
      const newPatient = addPatient(patientData);
      setPatients(prev => [...prev, newPatient]);
      return newPatient;
    } catch (error) {
      console.error('Erreur lors de l\'ajout du patient :', error);
      throw error;
    }
  }, []);

  // Mettre à jour un patient
  const updatePatientData = useCallback(async (patientId, patientData) => {
    try {
      const updatedPatient = updatePatient(patientId, patientData);
      setPatients(prev => prev.map(p => p.id === patientId ? updatedPatient : p));
      return updatedPatient;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du patient :', error);
      throw error;
    }
  }, []);

  // Supprimer un patient
  const deletePatientData = useCallback(async (patientId) => {
    try {
      deletePatient(patientId);
      setPatients(prev => prev.filter(p => p.id !== patientId));
    } catch (error) {
      console.error('Erreur lors de la suppression du patient :', error);
      throw error;
    }
  }, []);

  // Charger les données au démarrage
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const value = {
    // État
    patients,
    consultations,
    vitals,
    treatments,
    medicalHistory,
    observations,
    loading,
    error,
    
    // Actions - Load
    loadPatients,
    loadConsultations,
    loadVitals,
    loadTreatments,
    loadMedicalHistory,
    loadObservations,
    loadAllData,
    
    // Actions - Patients
    addPatient: addPatientData,
    updatePatient: updatePatientData,
    deletePatient: deletePatientData,
    
    // Actions - Consultations
    addConsultation: useCallback(async (consultationData) => {
      try {
        const newConsultation = addConsultation(consultationData);
        setConsultations(prev => [...prev, newConsultation]);
        return newConsultation;
      } catch (error) {
        console.error('Erreur lors de l\'ajout de la consultation :', error);
        throw error;
      }
    }, []),
    
    updateConsultation: useCallback(async (consultationId, consultationData) => {
      try {
        const updatedConsultation = updateConsultation(consultationId, consultationData);
        setConsultations(prev => prev.map(c => c.id === consultationId ? updatedConsultation : c));
        return updatedConsultation;
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la consultation :', error);
        throw error;
      }
    }, []),
    
    deleteConsultation: useCallback(async (consultationId) => {
      try {
        deleteConsultation(consultationId);
        setConsultations(prev => prev.filter(c => c.id !== consultationId));
      } catch (error) {
        console.error('Erreur lors de la suppression de la consultation :', error);
        throw error;
      }
    }, []),
    
    // Actions - Vitals
    addVitals: useCallback(async (vitalsData) => {
      try {
        const newVitals = addVitalsData(vitalsData);
        setVitals(prev => [...prev, newVitals]);
        return newVitals;
      } catch (error) {
        console.error('Erreur lors de l\'ajout des constantes vitales :', error);
        throw error;
      }
    }, []),
    
    updateVitals: useCallback(async (vitalsId, vitalsData) => {
      try {
        const updatedVitals = updateVitalsData(vitalsId, vitalsData);
        setVitals(prev => prev.map(v => v.id === vitalsId ? updatedVitals : v));
        return updatedVitals;
      } catch (error) {
        console.error('Erreur lors de la mise à jour des constantes vitales :', error);
        throw error;
      }
    }, []),
    
    deleteVitals: useCallback(async (vitalsId) => {
      try {
        deleteVitalsData(vitalsId);
        setVitals(prev => prev.filter(v => v.id !== vitalsId));
      } catch (error) {
        console.error('Erreur lors de la suppression des constantes vitales :', error);
        throw error;
      }
    }, []),
    
    // Actions - Treatments
    addTreatment: useCallback(async (treatmentData) => {
      try {
        const newTreatment = addTreatment(treatmentData);
        setTreatments(prev => [...prev, newTreatment]);
        return newTreatment;
      } catch (error) {
        console.error('Erreur lors de l\'ajout du traitement :', error);
        throw error;
      }
    }, []),
    
    updateTreatment: useCallback(async (treatmentId, treatmentData) => {
      try {
        const updatedTreatment = updateTreatment(treatmentId, treatmentData);
        setTreatments(prev => prev.map(t => t.id === treatmentId ? updatedTreatment : t));
        return updatedTreatment;
      } catch (error) {
        console.error('Erreur lors de la mise à jour du traitement :', error);
        throw error;
      }
    }, []),
    
    deleteTreatment: useCallback(async (treatmentId) => {
      try {
        deleteTreatment(treatmentId);
        setTreatments(prev => prev.filter(t => t.id !== treatmentId));
      } catch (error) {
        console.error('Erreur lors de la suppression du traitement :', error);
        throw error;
      }
    }, []),
    
    // Actions - Medical History
    addMedicalHistory: useCallback(async (historyData) => {
      try {
        const newHistory = addMedicalHistory(historyData);
        setMedicalHistory(prev => [...prev, newHistory]);
        return newHistory;
      } catch (error) {
        console.error('Erreur lors de l\'ajout de l\'antécédent médical :', error);
        throw error;
      }
    }, []),
    
    updateMedicalHistory: useCallback(async (historyId, historyData) => {
      try {
        const updatedHistory = updateMedicalHistory(historyId, historyData);
        setMedicalHistory(prev => prev.map(h => h.id === historyId ? updatedHistory : h));
        return updatedHistory;
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'antécédent médical :', error);
        throw error;
      }
    }, []),
    
    deleteMedicalHistory: useCallback(async (historyId) => {
      try {
        deleteMedicalHistory(historyId);
        setMedicalHistory(prev => prev.filter(h => h.id !== historyId));
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'antécédent médical :', error);
        throw error;
      }
    }, []),
    
    // Actions - Observations
    addObservation: useCallback(async (observationData) => {
      try {
        const newObservation = addObservation(observationData);
        setObservations(prev => [...prev, newObservation]);
        return newObservation;
      } catch (error) {
        console.error('Erreur lors de l\'ajout de l\'observation :', error);
        throw error;
      }
    }, []),
    
    updateObservation: useCallback(async (observationId, observationData) => {
      try {
        const updatedObservation = updateObservation(observationId, observationData);
        setObservations(prev => prev.map(o => o.id === observationId ? updatedObservation : o));
        return updatedObservation;
      } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'observation :', error);
        throw error;
      }
    }, []),
    
    deleteObservation: useCallback(async (observationId) => {
      try {
        deleteObservation(observationId);
        setObservations(prev => prev.filter(o => o.id !== observationId));
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'observation :', error);
        throw error;
      }
    }, []),
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => {
  const context = React.useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext doit être utilisé dans un DataProvider');
  }
  return context;
};

export default DataContext;